import { ReactNode, useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  ColorValue,
  InputModeOptions,
  KeyboardTypeOptions,
  Platform,
  StyleProp,
  ViewStyle,
} from 'react-native';
import { TextInput } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { TextInput as TextInputCropCircle } from '../crop-circle/Components/TextInput/TextInput';
import { AutocompleteTextInput } from './formTypes';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type ControlledTextInputProps = {
  control: any;
  name: string;
  placeholder?: string;
  editable?: boolean;
  autoFocus?: boolean;
  textArea?: boolean;
  textColor?: string;
  innerRef?: any;
  showClearValue?: boolean;
  outlinedTextInput?: boolean;
  keyboardType?: KeyboardTypeOptions;
  autoComplete?: AutocompleteTextInput;
  height?: number;
  disableInnerStyle?: boolean;
  testID?: string;
  isLowerCase?: boolean;
  backgroundColor?: ColorValue;
  width?: number | '100%';
  inputInnerStyle?: StyleProp<ViewStyle>;
  onFocus?: () => void;
  autoExpand?: boolean;
  numberOfLines?: number;
  hideBorder?: boolean;
  /** Custom right icon element passed to Crop Circle TextInput (replaces clear button) */
  rightIcon?: ReactNode;
};

export const ControlledTextInput = (props: ControlledTextInputProps) => {
  const editable = props.editable === undefined ? true : props.editable;

  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field: { onChange, onBlur, value } }) => (
        <TextInputCropCircle
          testID={props.testID}
          onChangeText={(text) =>
            onChange(props?.isLowerCase ? text.toLowerCase() : text)
          }
          onBlur={onBlur}
          value={value}
          placeholder={props.placeholder}
          clearable={props.showClearValue}
          disabled={!editable}
          autoFocus={props.autoFocus}
          textColor={props.textColor}
          ref={props.innerRef}
          keyboardType={props.keyboardType}
          autoComplete={props.autoComplete}
          height={props.height}
          hideBorder={props.hideBorder}
          backgroundColor={props.backgroundColor}
          width={props.width}
          autoExpand={props.autoExpand}
          numberOfLines={props.numberOfLines ? props.numberOfLines : undefined}
          onFocus={() => props?.onFocus?.()}
          textArea={props.textArea}
          rightIcon={props.rightIcon}
        />
      )}
    />
  );
};

export type StyledTextInputProps = Omit<
  ControlledTextInputProps,
  'control' | 'name'
> & {
  onChangeText: (text: string) => void;
  onBlur: () => void;
  onFocus?: () => void;
  onSubmitEditing?: () => void;
  value?: any;
  left?: ReactNode;
  right?: ReactNode;
  outlineStyle?: StyleProp<ViewStyle>;
  disableInnerStyle?: boolean;
  height?: number;
  textColor?: string;
  keyboardType?: KeyboardTypeOptions;
  autoComplete?: AutocompleteTextInput;
  width?: number | '100%';
  inputMode?: InputModeOptions | undefined;
  backgroundColor?: ColorValue;
  inputInnerStyle?: StyleProp<ViewStyle>;
  pointerEvents?: 'none' | 'auto' | 'box-none' | 'box-only';
  render?: any;
  style?: StyleProp<any>;
  returnKeyType?: 'done' | 'go' | 'next' | 'search' | 'send';
  disabled?: boolean;
  label?: string;
};

export const StyledTextInput = (props: StyledTextInputProps) => {
  const editable = props.editable === undefined ? true : props.editable;
  const [isFocused, setIsFocused] = useState(false);

  return (
    <TextInput
      label={props.label}
      testID={props.testID}
      left={props.left}
      right={props.right}
      mode="outlined"
      textColor={props.textColor}
      style={[
        {
          height: props.height || 55,
          width: props.width || 'auto',
          backgroundColor: props?.backgroundColor ?? Colors.role.surface,
        },
        props.outlinedTextInput && {
          borderWidth: 1,
          borderColor: isFocused
            ? Colors.role.textStrong
            : Colors.role.textSecondary,
          borderRadius: 4,
        },
        props?.style ?? {},
      ]}
      contentStyle={[
        props.disableInnerStyle
          ? undefined
          : props.textArea
          ? [
              styledTextInputStyles.baseInputContent,
              styledTextInputStyles.areaInputContent,
            ]
          : [styledTextInputStyles.baseInputContent],
        Platform.select({
          android: props.textArea
            ? {
                paddingTop: 12,
                paddingBottom: 12,
              }
            : {
                paddingTop: 0,
                paddingBottom: 0,
              },
        }),
        props.backgroundColor
          ? { backgroundColor: props.backgroundColor }
          : undefined,
        props.inputInnerStyle,
      ]}
      value={props.value || ''}
      multiline={props.textArea}
      inputMode={props.inputMode}
      outlineStyle={
        props.outlinedTextInput
          ? undefined
          : props.outlineStyle ?? styledTextInputStyles.textOutline
      }
      onBlur={(e) => {
        setIsFocused(false);
        props.onBlur && props.onBlur();
      }}
      onFocus={(e) => {
        setIsFocused(true);
        props.onFocus && props.onFocus();
      }}
      onSubmitEditing={props.onSubmitEditing}
      onChangeText={(value) => props.onChangeText(value)}
      autoFocus={props.autoFocus}
      placeholder={props.placeholder}
      editable={editable}
      placeholderTextColor={Colors.role.fillDisabled}
      ref={props.innerRef}
      keyboardType={props.keyboardType}
      autoComplete={props.autoComplete}
      pointerEvents={props.pointerEvents}
      render={props.render}
      returnKeyType={props.returnKeyType}
      disabled={props.disabled}
    />
  );
};

export const styledTextInputStyles = themedStyleSheet((role) => ({
  areaInputContent: {
    height: 100,
    paddingTop: 12,
  },
  baseInputContent: {
    paddingHorizontal: 20,
    backgroundColor: role.surface,
    borderRadius: 4,
    width: 'auto',
  },
  textOutline: {
    borderWidth: 0,
  },
  counterButton: {
    width: 64,
    height: '100%',
    backgroundColor: role.surface,
    borderRadius: 0,
    borderWidth: 0,
    margin: 0,
  },
}));
